import { createContext, ReactNode, useContext } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Box } from '../box';
import { Flex } from '../flex';

const DrawerOverlay = () => {
  const { onClose } = useDrawerContext();

  return (
    <Box
      position="absolute"
      zIndex={1000}
      bg="rgba(0, 0, 0, .45)"
      style={{ inset: 0, pointerEvents: 'auto' }}
      onClick={onClose}
    />
  );
};

const DrawerWrapper = ({ children }: { children: ReactNode }) => {
  /** This is for the right side */
  const styles = {
    top: 0,
    right: 0,
    bottom: 0,
    boxShadow:
      '-6px 0 16px -8px #00000014, -9px 0 28px #0000000d, -12px 0 48px 16px #00000008',
  };

  return (
    <Box
      position="absolute"
      zIndex={1000}
      width={['100%', '100%', 485]}
      style={{ transition: 'all .3s', ...styles }}
    >
      {children}
    </Box>
  );
};

const DrawerContent = ({ children }: { children: ReactNode }) => {
  return (
    <Box
      id="dc"
      width="100%"
      height="100%"
      overflow="auto"
      bg="#ffffff"
      style={{ pointerEvents: 'auto' }}
    >
      {children}
    </Box>
  );
};

const DrawerWrapperBody = ({ children }: { children: ReactNode }) => {
  return (
    <Flex flexDirection="column" width="100%" height="100%">
      {children}
    </Flex>
  );
};

const DrawerHeader = ({ children }: { children: ReactNode }) => {
  return (
    <Flex
      flex={0}
      alignItems="center"
      p={16}
      borderBottom="1px solid rgba(0,0,0,.06)"
    >
      {children}
    </Flex>
  );
};

const DrawerBody = ({ children }: { children: ReactNode }) => {
  return (
    <Flex
      flex={1}
      flexDirection="column"
      minWidth={0}
      minHeight={0}
      p={16}
      overflow="auto"
    >
      {children}
    </Flex>
  );
};

type DrawerContextProps = {
  isOpen: boolean;
  onClose?: () => void;
};

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);

  if (context === undefined) {
    throw new Error('useDrawerContext must be used under DrawerContext');
  }

  return context;
};

type DrawerProps = DrawerContextProps & { children: ReactNode };

const Drawer = (props: DrawerProps) => {
  const { isOpen, onClose, children } = props;

  return (
    <DrawerContext.Provider value={{ isOpen, onClose }}>
      {isOpen && (
        <RemoveScroll>
          <Box
            position="fixed"
            zIndex={1000}
            className={RemoveScroll.classNames.fullWidth}
            style={{ inset: 0, pointerEvents: 'none' }}
          >
            {children}
          </Box>
        </RemoveScroll>
      )}
    </DrawerContext.Provider>
  );
};

export {
  Drawer,
  DrawerWrapper,
  DrawerContent,
  DrawerWrapperBody,
  DrawerHeader,
  DrawerBody,
  DrawerOverlay,
};
